import { computed, Signal } from '@angular/core';
import { RunnerStep } from '../../models/test-run.model';
import { StepMap } from './step-map';

export interface StepDraft {
  actualResult: string;
  note: string;
}

/**
 * Der gespeicherte Stand eines Schritts als Entwurf. An einer Stelle, weil drei Wege ihn
 * brauchen — der angezeigte Entwurf, die Dirty-Erkennung und der PATCH — und ein
 * Auseinanderlaufen genau dort auffaellt, wo es keiner sieht: ein Entwurf gilt als offen,
 * der es nicht ist, oder umgekehrt.
 */
function serverDraft(step: RunnerStep): StepDraft {
  return { actualResult: step.actualResult ?? '', note: step.note ?? '' };
}

function sameText(a: StepDraft, b: StepDraft): boolean {
  return a.actualResult === b.actualResult && a.note === b.note;
}

export class RunnerDraftStore {
  private readonly steps = new StepMap<StepDraft>();

  /**
   * Je Schritt, nicht als eine Zeichenkette: die wanderte beim Wechsel in der Liste mit
   * und wurde am falschen Schritt abgeschickt — und ging beim Verlassen der Seite
   * verloren, weil der Guard sie nicht sah.
   */
  private readonly comments = new StepMap<string>();

  readonly dirty: Signal<boolean> = computed(() => !this.steps.empty || !this.comments.empty);

  draftOf(step: RunnerStep): StepDraft {
    return this.steps.get(step.id) ?? serverDraft(step);
  }

  pending(stepId: string): StepDraft | undefined {
    return this.steps.get(stepId);
  }

  patch(step: RunnerStep, change: Partial<StepDraft>): void {
    const next: StepDraft = { ...this.draftOf(step), ...change };
    if (sameText(next, serverDraft(step))) this.steps.remove(step.id);
    else this.steps.set(step.id, next);
  }

  /** Nur bei gleichem Text: was waehrend des Speicherns weiter getippt wurde, bleibt Entwurf. */
  clearIfSaved(updated: RunnerStep): void {
    const draft = this.steps.get(updated.id);
    if (draft && sameText(draft, serverDraft(updated))) this.steps.remove(updated.id);
  }

  unsavedFields(step: RunnerStep): Partial<StepDraft> {
    const draft = this.steps.get(step.id);
    if (!draft) return {};
    const saved = serverDraft(step);
    const fields: Partial<StepDraft> = {};
    if (draft.actualResult !== saved.actualResult) fields.actualResult = draft.actualResult;
    if (draft.note !== saved.note) fields.note = draft.note;
    return fields;
  }

  retainSteps(existing: ReadonlySet<string>): void {
    this.steps.retain(existing);
    this.comments.retain(existing);
  }

  commentOf(stepId: string): string {
    return this.comments.get(stepId) ?? '';
  }

  setComment(stepId: string, value: string): void {
    if (value === '') this.comments.remove(stepId);
    else this.comments.set(stepId, value);
  }

  clearComment(stepId: string): void {
    this.comments.remove(stepId);
  }
}
